// apps/web/src/pages/author/sections/WidgetInstances.tsx
//
// Phase 8 Plan 09 Task 3 — WidgetInstances form section (authr-01 / authr-03 / authr-05).
//
// recipe.widgets: Widget[]
//   each: { instanceId; templateId; name; placement; config }
// (RHF name path is `widgets` per packages/shared/src/schemas/recipe-v1.ts
// lines 55-80 — the section title is "Widget instances" per UI-SPEC but the
// underlying RHF path is `widgets`. Schema is source of truth, mirrors the
// IngredientGroups W13 pattern.)
//
// AUTHR-05 wiring:
//   - Add-widget Dialog Select populated from TEMPLATE_CATALOG keys, filtered
//     to exclude the un-rendered templates (see UNRENDERED_TEMPLATE_IDS below):
//     shopping-list-aggregator plus the three Phase 11 Plan 02 schema-only
//     registrations (scaling-matrix, option-tier-matrix, per-option-detail-card)
//     whose renderers land in later Phase 11 plans. This filter at the picker
//     layer ensures the closed-registry contract is enforced at the UI tier; the
//     dispatcher's fallback Alert is defense-in-depth (T-08-09-01, T-08-09-02).
//   - On Add confirm, the new instance's config is seeded via the W11 factory
//     `default{Name}Config()` exported by each per-template config sub-form
//     file. Single source of truth for defaults lives in the sub-form file —
//     this file consumes via the DEFAULT_FACTORIES Map below.
//   - Newly-added rows auto-expand to show the inline configSchema sub-form
//     (tracked via a local Set<instanceId> of open rows).
//
// authr-03 + authr-06 hygiene preserved (every useFieldArray row keys by
// field.id; no top-level useWatch; no formState reads).
//
// W11 — DEFAULT_FACTORIES is itself a Object.freeze + ReadonlyMap mirroring
// the closed-registry discipline of the Task 2 dispatcher. shopping-list-
// aggregator is absent (Phase 11 ownership).

import {
  type RecipeDefinitionV1,
  type SlotId,
  TEMPLATE_CATALOG,
  type TemplateId,
} from "@the-recipe-spread/shared";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Fieldset } from "@/components/ui/fieldset";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FieldArrayRow } from "../FieldArrayRow";
import { defaultBranchVariantConfig } from "../widget-config/branch-variant-config";
import { WidgetConfigSubForm } from "../widget-config/dispatcher";
import { defaultOptionSetConfig } from "../widget-config/option-set-config";
import { defaultRankedTierConfig } from "../widget-config/ranked-tier-config";
import { defaultSizeAndYieldConfig } from "../widget-config/size-and-yield-config";
import { defaultStaticNoteConfig } from "../widget-config/static-note-config";
import { defaultSummaryChecklistConfig } from "../widget-config/summary-checklist-config";

// W11 — single source of truth for per-template default configs lives in the
// per-template sub-form files (Task 1). This Map dispatches to those factories.
// Object.freeze + ReadonlyMap mirrors the closed-registry discipline of the
// Task 2 dispatcher.
const _factories = new Map<TemplateId, () => unknown>([
  ["option-set", defaultOptionSetConfig],
  ["summary-checklist", defaultSummaryChecklistConfig],
  ["static-note", defaultStaticNoteConfig],
  ["size-and-yield", defaultSizeAndYieldConfig],
  ["ranked-tier", defaultRankedTierConfig],
  ["branch-variant", defaultBranchVariantConfig],
]);

const DEFAULT_FACTORIES: ReadonlyMap<TemplateId, () => unknown> = Object.freeze(_factories);

// Templates registered in TEMPLATE_CATALOG but without an in-repo renderer yet.
// The author picker MUST NOT offer these — an author cannot configure a template
// that has no renderer (the dispatcher's fallback Alert is defense-in-depth).
// scaling-matrix shipped its renderer in Phase 11.4; option-tier-matrix +
// per-option-detail-card shipped theirs in Phase 11.5; shopping-list-aggregator
// shipped its renderer in Phase 11.8 (Plan 04) and was removed from this set. The
// set is now EMPTY — every cataloged template has a renderer. (shopping-list-
// aggregator is register-only: no author sub-form, so it is intentionally absent
// from DEFAULT_FACTORIES — the 11.4/11.5 register-only precedent.)
const UNRENDERED_TEMPLATE_IDS = new Set<TemplateId>([]);

// Picker options: all in-repo templates EXCLUDING the un-rendered ones. The filter
// is the authoritative closed-registry gate at the picker layer.
function getPickerOptions(): ReadonlyArray<{ value: TemplateId; label: string }> {
  return (Object.keys(TEMPLATE_CATALOG) as TemplateId[])
    .filter((id) => !UNRENDERED_TEMPLATE_IDS.has(id))
    .map((id) => ({ value: id, label: TEMPLATE_CATALOG[id].name }));
}

export function WidgetInstances(): React.ReactElement {
  const { control } = useFormContext<RecipeDefinitionV1>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "widgets",
  });

  // openRows tracks which instanceIds have their configuration panel expanded.
  // Newly-appended widgets are added here so the inline config sub-form is
  // visible immediately (per the plan's "newly-appended row auto-expands"
  // requirement).
  const [openRows, setOpenRows] = useState<ReadonlySet<string>>(new Set());
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [pickedTemplateId, setPickedTemplateId] = useState<TemplateId | undefined>(undefined);

  // WR-10 — track the pending focus timer so unmount / Dialog-close paths
  // can cancel it. Without this, a user who closes the Dialog or navigates
  // away before the 0ms timer fires would get an orphaned focus() call
  // potentially stealing focus from an unrelated element.
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (focusTimerRef.current !== null) {
        clearTimeout(focusTimerRef.current);
        focusTimerRef.current = null;
      }
    };
  }, []);

  const pickerOptions = getPickerOptions();

  const handleAddWidget = (): void => {
    if (pickedTemplateId === undefined) return;
    const factory = DEFAULT_FACTORIES.get(pickedTemplateId);
    // The `factory!` shape is guaranteed: the Select is sourced from the
    // closed-registry picker (shopping-list-aggregator filtered out), and
    // DEFAULT_FACTORIES has an entry for every other templateId.
    const newInstanceId = crypto.randomUUID();
    const newWidget = {
      instanceId: newInstanceId,
      templateId: pickedTemplateId,
      name: "New widget",
      placement: "main:floating-top" as SlotId,
      config: factory ? factory() : {},
    };
    append(newWidget);
    setOpenRows((prev) => {
      const next = new Set(prev);
      next.add(newInstanceId);
      return next;
    });
    setAddDialogOpen(false);
    setPickedTemplateId(undefined);
    // WR-10 — focus the new row's name input on the next tick — RHF mounts
    // the row asynchronously after append. Store the timer id in a ref so
    // the cleanup effect (unmount) and the Dialog-close path can clear it.
    // Drop any previously-pending timer first (rapid Add clicks).
    if (focusTimerRef.current !== null) {
      clearTimeout(focusTimerRef.current);
    }
    focusTimerRef.current = setTimeout(() => {
      focusTimerRef.current = null;
      const el = document.getElementById(`widget-${newInstanceId}-name`);
      if (el instanceof HTMLInputElement) {
        el.focus();
      }
    }, 0);
  };

  const toggleOpen = (instanceId: string): void => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(instanceId)) {
        next.delete(instanceId);
      } else {
        next.add(instanceId);
      }
      return next;
    });
  };

  return (
    <Fieldset legend="WIDGET INSTANCES">
      {fields.length === 0 ? (
        <EmptyState
          title="No widgets yet."
          cta={{
            label: "Add widget",
            onClick: () => setAddDialogOpen(true),
          }}
        />
      ) : (
        <>
          {fields.map((field, index) => (
            <WidgetInstanceRow
              key={field.id}
              index={index}
              instanceId={(field as unknown as { instanceId: string }).instanceId}
              templateId={(field as unknown as { templateId: string }).templateId}
              canMoveUp={index > 0}
              canMoveDown={index < fields.length - 1}
              onMoveUp={() => move(index, index - 1)}
              onMoveDown={() => move(index, index + 1)}
              onRemove={() => remove(index)}
              isOpen={openRows.has((field as unknown as { instanceId: string }).instanceId)}
              onToggle={() => toggleOpen((field as unknown as { instanceId: string }).instanceId)}
            />
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddDialogOpen(true)}
            className="self-start"
          >
            Add widget
          </Button>
        </>
      )}

      <Dialog
        open={addDialogOpen}
        onOpenChange={(open) => {
          setAddDialogOpen(open);
          if (!open) {
            setPickedTemplateId(undefined);
            // WR-10 — Dialog closed via Escape/Cancel: cancel any pending
            // focus timer the user might have queued by clicking Add then
            // immediately dismissing the Dialog.
            if (focusTimerRef.current !== null) {
              clearTimeout(focusTimerRef.current);
              focusTimerRef.current = null;
            }
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add widget</DialogTitle>
            <DialogDescription>
              Pick a widget template. Configuration fields appear below.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium" htmlFor="widget-add-template-select">
              Template
            </label>
            <Select
              value={pickedTemplateId ?? ""}
              onValueChange={(v) => setPickedTemplateId(v as TemplateId)}
            >
              <SelectTrigger id="widget-add-template-select">
                <SelectValue placeholder="Choose a template…" />
              </SelectTrigger>
              <SelectContent>
                {pickerOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleAddWidget}
              disabled={pickedTemplateId === undefined}
            >
              Add widget
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Fieldset>
  );
}
WidgetInstances.displayName = "WidgetInstances";

interface WidgetInstanceRowProps {
  index: number;
  instanceId: string;
  templateId: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  isOpen: boolean;
  onToggle: () => void;
}

function WidgetInstanceRow({
  index,
  instanceId,
  templateId,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRemove,
  isOpen,
  onToggle,
}: WidgetInstanceRowProps): React.ReactElement {
  const { control } = useFormContext<RecipeDefinitionV1>();
  // Per-path useWatch — narrow subscription to a single field for the Dialog
  // interpolation. No top-level useWatch (Pitfall 1 + 2 hygiene).
  const widgetName = useWatch({
    control,
    name: `widgets.${index}.name`,
  });
  const nameForDialog =
    typeof widgetName === "string" && widgetName.length > 0 ? widgetName : `Widget ${index + 1}`;

  return (
    <FieldArrayRow
      singular="widget"
      index={index}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      onRemove={onRemove}
      deleteDialogTitle="Delete widget?"
      deleteDialogBody={`${nameForDialog} will be removed from this recipe. This cannot be undone before saving.`}
      deleteConfirmLabel="Delete widget"
    >
      <Fieldset legend={nameForDialog}>
        <FormField
          control={control}
          name={`widgets.${index}.instanceId`}
          render={({ field: f }) => (
            <FormItem data-field-path={`widgets.${index}.instanceId`}>
              <FormLabel>Instance id (read-only)</FormLabel>
              <FormControl>
                <Input {...f} value={f.value ?? ""} readOnly />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`widgets.${index}.templateId`}
          render={({ field: f }) => (
            <FormItem data-field-path={`widgets.${index}.templateId`}>
              <FormLabel>Template</FormLabel>
              {/*
                Template selector is rendered DISABLED post-creation —
                switching templateId would invalidate the existing config;
                the UI-SPEC does not allow templateId edits in v1. Authors
                must delete the widget and re-add with the desired template.
                If a recipe is paste-loaded with a mismatched templateId/config
                pair, the existing Phase 6 widget-config-invalid Alert
                (template-renderer.tsx) handles it gracefully.
              */}
              <Select value={(f.value as string) ?? templateId} onValueChange={f.onChange} disabled>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={templateId} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {(Object.keys(TEMPLATE_CATALOG) as TemplateId[]).map((t) => (
                    <SelectItem key={t} value={t}>
                      {TEMPLATE_CATALOG[t].name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                Pick a widget template. Configuration fields appear below.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`widgets.${index}.name`}
          render={({ field: f }) => (
            <FormItem data-field-path={`widgets.${index}.name`}>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...f} id={`widget-${instanceId}-name`} value={f.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`widgets.${index}.placement`}
          render={({ field: f }) => (
            <FormItem data-field-path={`widgets.${index}.placement`}>
              <FormLabel>Placement</FormLabel>
              <FormControl>
                <Input {...f} value={f.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-controls={`widget-${instanceId}-config`}
            className="inline-flex items-center gap-1 text-sm text-foreground font-medium hover:text-muted-foreground self-start"
          >
            {isOpen ? (
              <ChevronDown size={14} aria-hidden />
            ) : (
              <ChevronRight size={14} aria-hidden />
            )}
            Configuration
          </button>
          {isOpen ? (
            <div id={`widget-${instanceId}-config`}>
              <WidgetConfigSubForm
                templateId={templateId}
                instanceId={instanceId}
                basePath={`widgets.${index}.config`}
              />
            </div>
          ) : null}
        </div>
      </Fieldset>
    </FieldArrayRow>
  );
}
